import {
  WSCard,
  WSDataItem,
  WSGrid,
  WSList
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useDynamicFormEngineConfig } from "../context/DynamicFormEngineConfigContext";
import { IFormNote, IFormSchemaField } from "../types";
import { formatReadOnlyValue } from "../utils/formatReadOnlyValue";
import { isSensitiveField } from "../utils/isSharedPiiField";
import { DynamicFormNote } from "./DynamicFormNote";

type Props = {
  fields: IFormSchemaField[];
  /** Card title — the identity section's title on the wizard's first page. */
  title?: string;
  /** Gray helper copy under the card title. */
  description?: string;
  /** Section-level notes (e.g. the identity section's) rendered in the card. */
  notes?: IFormNote[];
};

/**
 * Groups the form's pre-filled (read-only) fields into a single card the
 * user verifies — values are stamped server-side from previously collected
 * forms / live records and aren't editable here. When the host configures
 * `onEditPrefilledData` (DynamicFormEngineConfig), a "Need to make a change?"
 * link sits inline with the card title (header action slot) pointing at the
 * host's explanation of where each kind of value can actually be changed. On
 * the wizard's first page this is the merged "Your information" identity card;
 * single-page forms keep the "Pre-filled data" presentation.
 */
export const PrefilledDataCard: React.FC<Props> = ({
  fields,
  title = "Pre-filled data",
  description = "Verify the data we will be using from previously collected forms.",
  notes = []
}) => {
  const { onEditPrefilledData } = useDynamicFormEngineConfig();

  if (fields.length === 0) return null;

  return (
    <WSCard
      header={{
        label: {
          text: title,
          helperText: description
        },
        ...(onEditPrefilledData
          ? {
              value: {
                action: {
                  label: "Need to make a change?",
                  onClick: onEditPrefilledData
                }
              }
            }
          : {})
      }}
    >
      <WSList gap="M">
        {notes.map((note, i) => (
          <DynamicFormNote key={`prefilled-note-${i}`} note={note} />
        ))}
        <WSGrid gutter="M">
          {fields.map(field => (
            <WSGrid.Item key={field.key} span={{ s: "12", m: "6" }}>
              <WSDataItem
                label={{
                  text: field.label,
                  helperText: field.description ?? field.ui?.helpText
                }}
                value={formatReadOnlyValue(field.prefill?.value)}
                fsExclude={isSensitiveField(field)}
              />
            </WSGrid.Item>
          ))}
        </WSGrid>
      </WSList>
    </WSCard>
  );
};
